import _ from 'lodash';
import React, { useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { Button, Grid, GridItem, LoadingDots, Message, PageHeader } from '@flos/react-ui';
import { useHippoText } from '../../hooks/useHippoText';
import { useSaveSelectedPackage } from '../../recoil/hooks/useSaveSelectedPackage';
import { getOnsaCurrentProductSelector } from '../../recoil/selectors/getOnsaCurrentProductSelector';
import { allCoverablesOnsaAtom } from '../../recoil/atoms/allCoverablesOnsaAtom';
import { useUpdateCoverableInformation } from '../../recoil/hooks/useUpdateCoverableInformation';
import { onsaProgressSelector } from '../../recoil/selectors/onsaProgressSelector';
import { getOnsaCoverable } from '../../utils/coverablesUtil';
import {
    getPackagesConfig,
    shouldShowModal,
    shouldDisableNavigation,
    shouldShowIndicativeOfferModal,
    shouldShowRoadAssistanceModal
} from './configs/LobPackagesConfig';
import WizardPagetemplate from '../../templates/WizardPagetemplate';
import TotalPrice from '../../shared_components/TotalPrice/TotalPrice';
import CoveragePackageCards from './components/CoveragePackageCards';
import getPackageToggle from './components/PackageToggle/PackageToggleConfig';
import getCoveragePackageInput from './components/CoveragePackageInputs/CoveragePackageInputsConfig';
import getCoveragePackageHeader from './configs/CoveragePackageHeaderConfig';
import CallMeModal from '../../shared_components/CallMeModal/CallMeModal';
import useWizardNavigation from '../../hooks/useWizardNavigation';
import { track, trackViewProduct } from '../../utils/trackingUtil';
import { shoppingBasketOverlayAtom } from '../../recoil/atoms/shoppingBasketOverlayAtom';
import { isSavingPackageAtom } from '../../recoil/atoms/isSavingPackageAtom';
import { shouldCoverCriticalIllnessAtom } from '../../recoil/atoms/shouldCoverCriticalIllnessAtom';
import { refreshCustomerBenefitsAtom } from '../../recoil/atoms/refreshCustomerBenefitsAtom';
import type { WizardPageComponent } from '../../config/wizardPagesConfig';
import { useAsyncRecoilValue } from '../../hooks/useAsyncRecoilValue';
import { coveragePackagesSelector } from '../../recoil/selectors/coveragePackagesSelector';
import { coveragePackagePriceSelector } from '../../recoil/selectors/coveragePackagesPriceSelector';
import { additionalCoveragesSelector } from '../../recoil/selectors/additionalCoveragesSelector';
import { currentPackageGroupAtom } from '../../recoil/atoms/currentPackageGroupAtom';
import { hippoCoverageTextsSelector } from '../../recoil/selectors/hippoCoverageTextsSelector';
import ViewInfoModal from './ViewInfoModal/ViewInfoModal';

type InfoModalType = 'IndicativeOffer' | 'RoadAssistance' | 'None';

const CoveragePackagePage: WizardPageComponent = () => {
    const { wizardGotoStep } = useWizardNavigation();
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const getHippoText = useHippoText();
    const [onsaProgress, setOnsaProgress] = useRecoilState(onsaProgressSelector);
    const setIsSavingPackage = useSetRecoilState(isSavingPackageAtom);
    const resetCurrentPackageGroup = useResetRecoilState(currentPackageGroupAtom);
    const setShoppingBasketOverlayInfo = useSetRecoilState(shoppingBasketOverlayAtom);
    const refreshCustomerBenefitsSelector = useSetRecoilState(refreshCustomerBenefitsAtom);
    const [isLoading, setIsLoading] = useState(false);
    const [isSavingSelectedPackage, setIsSavingSelectedPackage] = useState(false);
    const [showInfoModal, setShowInfoModal] = useState<InfoModalType>('None');
    const [showCallMeModal, setShowCallMeModal] = useState(false);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const { lobCode = '', flavourName = '', fixedId = '' } = currentProduct ?? {};
    const shouldCoverCriticalIllness = useRecoilValue(shouldCoverCriticalIllnessAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const coverable =
        flavourName && fixedId ? getOnsaCoverable(coverables, flavourName, fixedId) : undefined;
    const isLobWithoutInput = getCoveragePackageInput(flavourName) === undefined;
    const packageGroupName = useRecoilValue(currentPackageGroupAtom);

    const PackageToggle = getPackageToggle(lobCode);
    const InputsComponent = getCoveragePackageInput(flavourName);

    const [coveragePackagesWithoutPrice, isPackageLoading] =
        useAsyncRecoilValue(coveragePackagesSelector);
    const [coveragePackagesWithPrice, isPriceLoading] = useAsyncRecoilValue(
        coveragePackagePriceSelector
    );
    const [additionalCoverages = [], areAdditionalCoveragesLoading] = useAsyncRecoilValue(
        additionalCoveragesSelector
    );
    const hasTrackedPackages = useRef(false);

    // Prefetch additional coverage texts
    useAsyncRecoilValue(
        hippoCoverageTextsSelector({
            coverageIds: additionalCoverages.map((cov) => cov.coverageId),
            termIds: coverable?.termIds || []
        })
    );

    const coveragePackageData = coveragePackagesWithPrice ?? coveragePackagesWithoutPrice ?? [];
    const { packages, defaultPackageName } = coverable
        ? getPackagesConfig(coverable, coveragePackageData, {
              shouldCoverCriticalIllness
          })
        : { packages: [], defaultPackageName: '' };

    const arePriceRequestsPending =
        isLoading || isPackageLoading || isPriceLoading || areAdditionalCoveragesLoading;

    const defaultPackageIndex =
        packages.findIndex((pack) => pack.name === defaultPackageName) !== -1
            ? packages.findIndex((pack) => pack.name === defaultPackageName)
            : 0;

    const [selectedPackageIndex, setSelectedPackageIndex] = useState(-1);
    const selectedPackage = packages[selectedPackageIndex] ?? packages[defaultPackageIndex];
    const { savePackage } = useSaveSelectedPackage(selectedPackage?.branchNumber, packageGroupName);

    const trackFormInputs = (): void => {
        if (!coverable) return;
        if (InputsComponent?.trackFormInputs) {
            const trackedFormInputs = InputsComponent?.trackFormInputs.map((trackingValue) => {
                const trackingKey = trackingValue.split('.')[0];
                return {
                    [trackingKey]: _.get(coverable, trackingValue)
                };
            });

            trackedFormInputs.push({ selectedPackage: selectedPackage.name });
            track(`${coverable.lobCode} CoveragePackagePage`, trackedFormInputs);
        } else {
            track(`${coverable.lobCode} CoveragePackagePage`, [
                { selectedPackage: selectedPackage.name }
            ]);
        }
    };

    const onOverlayGoNext = async (): Promise<void> => {
        setIsSavingPackage(true);
        resetCurrentPackageGroup();
        updateCoverableInformation({
            yearlyPrice: selectedPackage.price?.yearlyPrice,
            monthlyPrice: selectedPackage.price?.monthlyPrice,
            packageGroupName,
            packageName: selectedPackage.name,
            coverages: selectedPackage.coverages
        });
        await savePackage(selectedPackage.branchNumber, packageGroupName);
        refreshCustomerBenefitsSelector({});
        trackFormInputs();
    };

    const goNext = (): void => {
        if (shouldShowModal(coverables, selectedPackage, flavourName, fixedId)) {
            setShowCallMeModal(true);
        } else if (
            shouldShowIndicativeOfferModal(coverables, flavourName, fixedId, showInfoModal)
        ) {
            setShowInfoModal('IndicativeOffer');
        } else if (
            shouldShowRoadAssistanceModal(
                coverables,
                fixedId,
                selectedPackage,
                flavourName,
                showInfoModal
            )
        ) {
            setShowInfoModal('RoadAssistance');
        } else if (additionalCoverages?.length === 0) {
            updateCoverableInformation({
                yearlyPrice: selectedPackage.price?.yearlyPrice,
                monthlyPrice: selectedPackage.price?.monthlyPrice
            });
            setShoppingBasketOverlayInfo({
                isButtonDisplayed: true,
                isOverlayOpen: true,
                beforeGoNext: onOverlayGoNext
            });
        } else {
            setIsSavingSelectedPackage(true);
            updateCoverableInformation({
                yearlyPrice: selectedPackage.price?.yearlyPrice,
                monthlyPrice: selectedPackage.price?.monthlyPrice,
                packageGroupName,
                packageName: selectedPackage.name,
                coverages: selectedPackage.coverages
            });
            void savePackage(selectedPackage.branchNumber, packageGroupName).then(() => {
                setIsSavingSelectedPackage(false);
                refreshCustomerBenefitsSelector({});
                trackFormInputs();
                wizardGotoStep('AdditionalCoveragePage');
            });
        }
    };

    const goPrevious = (): void => {
        setIsSavingSelectedPackage(true);
        void savePackage(selectedPackage.branchNumber, packageGroupName).then(() => {
            updateCoverableInformation({
                yearlyPrice: undefined,
                monthlyPrice: undefined
            });
            wizardGotoStep(onsaProgress.prevStep);
            setOnsaProgress((prevStep) => ({
                ...prevStep,
                currentStep: onsaProgress.totalSteps,
                totalSteps: onsaProgress.totalSteps
            }));
            setIsSavingSelectedPackage(false);
        });
    };

    if (!currentProduct || !coverable) {
        return <></>;
    }
    const shouldDisable = shouldDisableNavigation(coverable);
    const headerSectionTexts = getCoveragePackageHeader(coverable, packages.length) ?? {
        headerText: 'DSS-COVERAGE-PACKAGE-PAGE-HEADER'
    };

    if (!hasTrackedPackages.current && !arePriceRequestsPending) {
        const defaultSelectedPackage = packages[defaultPackageIndex];
        if (defaultSelectedPackage) {
            const defaultProduct = {
                ...coverable,
                packageName: defaultSelectedPackage.name,
                coverages: defaultSelectedPackage.coverages,
                yearlyPrice: defaultSelectedPackage.price?.yearlyPrice,
                monthlyPrice: defaultSelectedPackage.price?.monthlyPrice
            };
            trackViewProduct(defaultProduct);
            hasTrackedPackages.current = true;
        }
    }

    return (
        <React.Fragment>
            <WizardPagetemplate shouldHidePadding hidePrevious hideNext>
                <PageHeader
                    align="center"
                    title={getHippoText(headerSectionTexts.headerText)}
                    subtitle={
                        headerSectionTexts.subHeaderText !== undefined
                            ? getHippoText(headerSectionTexts.subHeaderText)
                            : undefined
                    }
                />
                {PackageToggle && (
                    <PackageToggle
                        isLoading={arePriceRequestsPending || isSavingSelectedPackage}
                        setIsLoading={setIsLoading}
                    />
                )}
                {isPackageLoading || packages.length === 0 ? (
                    <div className="u-display-flex u-flex-justify-center u-spacing-stack-bottom-6">
                        <LoadingDots centered={false} />
                    </div>
                ) : (
                    <CoveragePackageCards
                        packages={packages}
                        defaultPackage={
                            !packages[selectedPackageIndex]
                                ? defaultPackageIndex
                                : selectedPackageIndex
                        }
                        isLoading={arePriceRequestsPending}
                        setSelectedPackageIndex={setSelectedPackageIndex}
                    />
                )}
                <Grid gap={7} className="u-spacing-stack-xl u-spacing-inset-l--stretch-max">
                    {InputsComponent?.Component && (
                        <InputsComponent.Component
                            isLoading={arePriceRequestsPending || isSavingSelectedPackage}
                            setIsLoading={setIsLoading}
                        />
                    )}
                    <GridItem colStart={isLobWithoutInput ? 4 : 8} span={isLobWithoutInput ? 6 : 5}>
                        {lobCode === 'TRA' && (
                            <Message theme={'success'} className="u-spacing-stack-m">
                                <p
                                    dangerouslySetInnerHTML={{
                                        __html: getHippoText(
                                            'DSS-COVERAGE-PACKAGE-PAGE-TRAVEL-COVER-INFORMATION'
                                        )
                                    }}
                                />
                            </Message>
                        )}
                        <div className="u-spacing-inset-l u-border-ice u-border-radius-m">
                            <TotalPrice
                                totalPrice={
                                    !arePriceRequestsPending
                                        ? selectedPackage?.price?.monthlyPrice?.amount
                                        : 0
                                }
                                showYearlyPriceField
                                displayTaxesSubtitle
                                paymentFrequencyPostfix={'/md.'}
                                showCarTaxes={lobCode === 'MVP'}
                                showTaxesOnlyForLob={lobCode}
                            />
                        </div>
                        {lobCode === 'MVP' && (
                            <p className="u-spacing-inset-top-5 u-spacing-stack-none">
                                {getHippoText('DSS-COVERAGE-PACKAGE-PAGE-PRICE-CHANGE-TEXT')}
                            </p>
                        )}
                        <div className="u-spacing-inset-top-7 u-spacing-inset-bottom-12 u-spacing-inset-bottom-none@sm">
                            <Button
                                disabled={
                                    shouldDisable ||
                                    arePriceRequestsPending ||
                                    isSavingSelectedPackage
                                }
                                onClick={goNext}
                            >
                                {getHippoText('DSS-COMMON-CONTINUE')}
                            </Button>
                            <Button
                                disabled={arePriceRequestsPending || isSavingSelectedPackage}
                                onClick={goPrevious}
                                theme={'ghost'}
                            >
                                {getHippoText('DSS-COMMON-BACK')}
                            </Button>
                        </div>
                    </GridItem>
                </Grid>
            </WizardPagetemplate>
            <CallMeModal
                activityCode="dss_call_request"
                showModal={showCallMeModal}
                closeModal={() => {
                    setShowCallMeModal(false);
                }}
            />
            {lobCode === 'UVP' && (
                <ViewInfoModal
                    closeModal={() => {
                        setShowInfoModal('None');
                    }}
                    showInfoModal={showInfoModal}
                    goNext={() => {
                        setShowInfoModal('None');
                        goNext();
                    }}
                />
            )}
        </React.Fragment>
    );
};

export default CoveragePackagePage;
